/* eslint-disable no-useless-escape */
/* eslint-disable no-extra-parens */
// @ts-expect-error
import uuid  from 'uuid';
import { UserProvider } from '../../common/providers/userProvider';
import { getJsonCookieByKey, setJsonCookie } from '../../common/utilities/cookie';
import { parseIsoDateToDateTimeObject } from '../../common/utilities/dateTimeUtils';
import { getQueryParameterByName } from '../../common/utilities/queryParameters';
import { dataSources, getSourceProviderId } from '../../common/utilities/sourceProviderUtilities';
import { SimpleStorage } from '../../common/utilities/webStorage';
import { AppConfigType, IAppState, PlatformKeys, PlatformType, User } from '../../components/common/interfaces/types';
import { DeviceInfo } from './appStateTypes';

//These regular expressions and the getOs/getOsVersion functions need to stay in sync with UserFilterAttribute.cs

const userStateCookieDaysToExpire = 365*30;
const nAgt = window.navigator.userAgent;
const breakingNewsTestCategory = 'air-quality';
const defaultWebBackendBaseUrl = 'https://www.weatherbug.com';
const userStateCookieName = 'UserState-v1';
const appInstanceIdName = 'web-weatherbug:appInstanceId';

export const setAppInstanceIdInUserCookie = (cookieName: string, appInstanceId: string, cookieAccessIsEnabled: boolean): void => {
    if (appInstanceId && cookieAccessIsEnabled)
    {
        const userCookie = getJsonCookieByKey(cookieName);
        userCookie.DEVICEID = appInstanceId;

        setJsonCookie(cookieName, userCookie, userStateCookieDaysToExpire);
    }
};

export const getAppInstanceIdFromUserCookie = (cookieName: string, cookieAccessIsEnabled: boolean): string => {
    let appInstanceId = '';
    if (cookieAccessIsEnabled && cookieName) {
        const userCookie = getJsonCookieByKey(cookieName);
        if (userCookie?.DEVICEID)
        {
            appInstanceId = userCookie.DEVICEID;
        }  
    }
    return appInstanceId;
};

export const isTaboolaAdEnabled = (complianceRequirementsDisableAds: boolean, isTaboolaDisabled: boolean, taboolaLightHouseQueryParamDisableValue: string): boolean => {
    let enableTaboola = true;
    const disableTaboolQueryParams = getQueryParameterByName('s');

    if ((disableTaboolQueryParams?.toLocaleLowerCase() === taboolaLightHouseQueryParamDisableValue?.toLocaleLowerCase())
        || complianceRequirementsDisableAds
        || isTaboolaDisabled) {
        enableTaboola = false;
    }

    return enableTaboola;
};

export const getAreTermsAndConditionsNotificationEnabled = (isNotificationsEnabled: boolean, platform: PlatformType, platformCode: PlatformKeys): boolean => {
    let enableNotifications = isNotificationsEnabled;

    if (enableNotifications && platformCode)
    {
        const isTermsAndConditionsNotificationEnabled = platform[platformCode]?.TermsAndConditionsNotification;
        enableNotifications  =  typeof isTermsAndConditionsNotificationEnabled === 'boolean' ? isTermsAndConditionsNotificationEnabled :  true ;
    }

    return enableNotifications;
};

export const getAreUseOfCookiesNotificationEnabled = (isNotificationsEnabled: boolean, platform: PlatformType, platformCode: PlatformKeys): boolean => {
    let enableNotifications = isNotificationsEnabled;

    if (enableNotifications && platformCode)
    {
        const isCookieNotificationEnabled = platform[platformCode]?.UseOfCookiesNotification;
        enableNotifications  = typeof isCookieNotificationEnabled === 'boolean'
            ? isCookieNotificationEnabled
            : true;
    }

    return enableNotifications;
};

export const getAreNotificationsEnabled = (platform: PlatformType, platformCode: PlatformKeys): boolean => {
    let enableNotifications = true;

    if (platformCode)
    {
        const isNotificationEnabled = platform[platformCode]?.Notifications;
        enableNotifications  = typeof isNotificationEnabled === 'boolean'
            ? isNotificationEnabled
            : true;
    }

    return enableNotifications;
};

export const getAreAdsEnabled = (isComplianceRequirementsDisableAd: boolean, areAdsDisable: boolean, platform: PlatformType, platformCode: PlatformKeys): boolean => {
    let enableAds = true;

    if (isComplianceRequirementsDisableAd || areAdsDisable
    )
    {
        enableAds = false;
    }

    if (enableAds && platformCode)
    {
        const areAdsEnabledForPlatform = platform[platformCode]?.Ads;
        enableAds  = typeof areAdsEnabledForPlatform === 'boolean'
            ? areAdsEnabledForPlatform
            : true;
    }
    return enableAds;
};

export const getCountryCodeFromLocale = (locale: string): string | null => {
    const localeParts = locale.split('-');
    return localeParts?.length > 0 ? localeParts[1] : 'us';
};

export const getLanguageCodeFromLocale = (locale: string): string | null => {
    const localeParts  = locale.split('-');
    return localeParts?.length > 0 ? localeParts[0] : 'en';
};

export const getUserLocale = (): string => {
    return window?.WB?.locale || 'en-us';
};

export const getOsVersion = (): string | undefined => {
    const deviceInfo = getDeviceInfoFromCookie();
    return deviceInfo?.osVersion;
};

export const getOs = (): string | undefined => {
    const deviceInfo = getDeviceInfoFromCookie();
    return deviceInfo?.os;
};

export const getFormFactor = (): string | undefined => {
    const deviceInfo = getDeviceInfoFromCookie();
    return deviceInfo?.formFactor;
};

const getDeviceInfoFromCookie = (): DeviceInfo => {
    const userStateCookie = getJsonCookieByKey(userStateCookieName);
    const { OS: os, OSV: osVersion, FORMFACTOR: formFactor } = userStateCookie ?? {};
    return { os, osVersion, formFactor };
};

export const userUpdateChecks = async (
    appConfig: AppConfigType, 
    userObj: User, 
    storage: InstanceType<typeof SimpleStorage>, 
    allowStorageOrCookieAccess: boolean): Promise<{ user: User; appInstanceId: string; }> => {

    const kochava = window.kochava || {};
    const { MinimumAppInstanceRegistrationDateUtc, BotAppInstanceId } = appConfig;
    const os = getOs();
    const osVersion = getOsVersion();
    let user = userObj;
    const existingAppInstanceId = storage?.getItem(appInstanceIdName);
    const appInstanceIdFromUser = getAppInstanceIdFromUserCookie(userStateCookieName, allowStorageOrCookieAccess);
    let appInstanceId = appInstanceIdFromUser;

    if (existingAppInstanceId)
    {
        appInstanceId = existingAppInstanceId;
    }
    else
    {
        storage?.setItem(appInstanceIdName, appInstanceId);
    }

    if (appInstanceIdFromUser !== appInstanceId)
    {
        // Set deviceId in User cookie
        setAppInstanceIdInUserCookie(userStateCookieName, appInstanceId, allowStorageOrCookieAccess);
    }

    const userAppInstanceIdRegistrationDateUTCInstance = parseIsoDateToDateTimeObject(user.AppInstanceIdRegistrationDateUtc)?.toUTC();
    const minimumAppInstanceRegistrationDateUtc = parseIsoDateToDateTimeObject(MinimumAppInstanceRegistrationDateUtc)?.toUTC();
    
    const appInstanceUTCDiff = userAppInstanceIdRegistrationDateUTCInstance?.isValid && minimumAppInstanceRegistrationDateUtc?.isValid 
        ? userAppInstanceIdRegistrationDateUTCInstance < minimumAppInstanceRegistrationDateUtc
        : false;

    if (appInstanceId.toUpperCase() !== BotAppInstanceId.toUpperCase()) {
        if (!user?.AppInstanceIdRegistrationDateUtc
            || !userAppInstanceIdRegistrationDateUTCInstance?.isValid
            || appInstanceUTCDiff) {
            user.Os = os as string;
            user.OsVersion = osVersion as string;
            user.Id = uuid();
            user.DeviceId = uuid();
            try {
                const response = await UserProvider.updateUser(user);
                const newUser = response?.data as User;
                if (newUser) {
                    appInstanceId = newUser.DeviceId;
                    user = { ...newUser };
                    storage?.setItem(appInstanceIdName, newUser.DeviceId);
                }
            } catch (error) {
                console.error('Error updating User', error);
            }
            
        } else if (user.Os?.toUpperCase() !== os?.toUpperCase() || user.OsVersion?.toUpperCase() !== osVersion?.toUpperCase()) {
            user.Os = os as string;
            user.OsVersion = osVersion as string;
            try {
                user = await UserProvider.updateUser(user) as unknown as User;
            } catch (error) {
                console.error('Error updating User', error);
            }
            
            storage?.setItem(appInstanceIdName, appInstanceId);
        }

        kochava?.identify?.({ appInstanceId });
    }

    return {
        user,
        appInstanceId
    };
};

export const generateAppState = (
    appConfig: AppConfigType, 
    appPlatformCode: PlatformKeys, 
    userObj: User,
    activeRoute: string,
    isComplianceRequirementsDisableAd: boolean, 
    areAdsDisable: boolean, 
    appPlatform: PlatformType,
    allowStorageOrCookieAccess: boolean ): IAppState => {
    const {
        AndroidAppDownloadLink,
        AndroidSmartAppBannerURL,
        ApplicationName,
        AppVersion,
        BaseURLs,
        BotAppInstanceId,
        DesktopAppDownloadLink,
        EducationVideoPageURL,
        EnableDesktopAppDownloadLink,
        Environment,
        DefaultMapLayerId,
        DefaultAdRefeshIntervalSeconds,
        LeadingEdgeDebounceDelay,
        MapsPageRefreshTimer,
        FeaturedVideoPrerollPlacementId,
        ForecastIconContent,
        HurricaneOutlookDetailsImages,
        PageContentDoesNotRequireActiveLocationData,
        TrackerExclusionList,
        IOSAppDownloadLink,
        IosSmartAppBannerURL,
        KochavaClickURL,
        MacosAppDownloadLink,
        NationalOutlookVideoPrerollPlacementId,
        Pulse,
        SparkMap,
        StoriesVideoPrerollPlacementId,
        SupportEmailAddress,
        Taboola,
        TaboolaInfo,
        VideoAdRules,
        VideoPrerollAdClient,
        VideoPrerollAutoplayAdsMuted,
        VideoPrerollOffset,
        VideoPrerollSkipOffset,
        VideoPreRollUrls,
        VideoPrerollVPaidControls,
        WebBackendBaseURL,
        WB_AppKey,
        EnableBreakingNewsTest,
        EnableNewSmartBanner,
        EnableNewFooterUIWithCMPIntegration,
        UsPrivacyManagerId,
        SourcePointCMPAccountId,
        SourcePointCMPPropertyId,
        SourcePointCMPPropertyHref,
        SourcePointCMPBaseEndpoint,
        SourcePointCMPCampaignEnv,
        EmailOptInDialog,
        PressLinkActive,
        AirQualityAttributionUrl,
        DisclosureRequestEmail,
        DeletionRequestEmail,
        PrivacyRightsEmail,
        CcpaInfoEmail,
        PrivacyRightsLink,
        PrivacyStatement,
        MyIdfaLink,
        AuthClientSettings,
        IsAuthV2Enabled,
        IsAqiV2Enabled,
        IsOutdoorSportsEnabled,
        NewPrivacyPolicyMockData,
        Persistent320x50AdForMobileWeb,
        IsComplianceApiV3Enabled,
        IsHurricaneSectionEnabled,
        Enable10DayForecastPageOnWeb2,
        EnableHourlyForecastPageOnWeb2,
        EnableTrafficCamerasPageOnWeb2,
        EnableWeatherCamerasPageOnWeb2,
        EnableMapsPageOnWeb2,
        EnableNewsLandingPageOnWeb2,
        EnableLifePollenPageOnWeb2,
        EnableLifeColdFluPageOnWeb2,
        EnableVideoLandingPageOnWeb2,
        EnableAlertsAQIPageOnWeb2,
        EnableAlertsPollenPageOnWeb2,
        EnableAlertsAllPageOnWeb2,
        EnableAlertsSevereStormPageOnWeb2,
        EnableAlertsLightningPageOnWeb2,
        EnableFireAlertsPageOnWeb2,
        EnableHurricaneAlertsPageOnWeb2,
        EnableAlertsWeatherPageOnWeb2,
        EnableSourceParameterOnStoriesLinks,
        EnableEducationPageOnWeb2,
        EnableAirQualityLandingPageOnWeb2,
        EnableFaqAndFaqGeneralPageOnWeb2,
        GTMediaCampaign,
        Comscore,
        Firebase,
        Boltive,
        GamTestAd,
        Platform,
        Mapbox,
        PollingTimes
    } = appConfig;
    
    const authId                        = Pulse.ID;
    const secret                        = Pulse.Secret;
    const adTargetingBaseUrl   = Environment === 'Staging' ? BaseURLs.AdTargeting.Stage : BaseURLs.AdTargeting.Prod;
    const alertsBaseUrl                 = BaseURLs.Alerts.Prod;
    const outdoorSportsBaseUrl          = BaseURLs.OutdoorSports.Prod;
    const notificationsBaseUrl = Environment === 'Staging' ? BaseURLs.Notifications.Stage : BaseURLs.Notifications.Prod;
    const aqiBaseUrl                    = BaseURLs.AQI.Prod;
    const hurricaneBaseUrl              = Environment === 'Staging' ? BaseURLs.Hurricane.Stage : BaseURLs.Hurricane.Prod;
    const hurricaneOutlookBaseUrl        = BaseURLs.HurricaneOutlook.Prod;
    const forecastBaseUrl               = BaseURLs.Forecast.Prod;
    const locationBaseUrl               = BaseURLs.Locations.Prod;
    const fireBaseUrl                   = BaseURLs.Fire.Prod;
    const obsBaseUrl                    = BaseURLs.Observations.Prod;
    const pollenBaseUrl                 = BaseURLs.Pollen.Prod;
    const lightningBaseUrl    	        = BaseURLs.Lightning.Prod;
    const loggingBaseUrl    	        = BaseURLs.Logging.Prod;
    const uvBaseUrl                     = BaseURLs.UV.Prod;
    const almanacBaseUrl                = BaseURLs.Almanac.Prod;
    const mapBaseUrl                    = BaseURLs.GivMetadata;
    const tileBaseUrl                   = BaseURLs.Giv;
    const contentMgmtBaseUrl            = BaseURLs.Content.Prod;
    const legalBaseUrl                  = BaseURLs.Legal.Prod;
    const storiesBaseUrl                = BaseURLs.Stories.Prod;
    const snowSkiBaseUrl                = BaseURLs.SnowSki.Prod;
    const videoBaseUrl                  = BaseURLs.Video.Prod;
    const feedbackBaseUrl               = BaseURLs.Feedback.Prod;
    const sparkMapBaseUrl               = SparkMap.Prod;
    const emLoginUrl                    = BaseURLs.EmLogin;
    const mapboxAccessToken             = Mapbox.PublicKey;
    const appId                         = ApplicationName || 'wbweb';
    const appVersion                    = AppVersion;
    const educationVideoPageURL         = EducationVideoPageURL;
    const kochavaClickURL               = KochavaClickURL;
    const webBackEndBaseURL             = WebBackendBaseURL || defaultWebBackendBaseUrl;
    const iosSmartAppBannerURL          = IosSmartAppBannerURL;
    const androidSmartAppBannerURL      = AndroidSmartAppBannerURL;
    const formFactor                    = getFormFactor();
    const platformCode                  = appPlatformCode;
    const adTarget                      = getQueryParameterByName('PC');
    const sourceProviderId              = getSourceProviderId(dataSources.queryParam);
    const user            	            = userObj;
    const doNotTrack      	            = navigator.doNotTrack;
    const forecastIconResourceList      = ForecastIconContent.IconContentResourceList;
    const forecastIconSize              = ForecastIconContent.ForecastIconSize;
    const forecastIconSet               = ForecastIconContent.ForecastIconSet;
    const emailOptInDialogIsEnabled     = EmailOptInDialog.Enabled;
    const emailOptInDialogScriptSrcUrl  = EmailOptInDialog.ScriptSourceUrl;
    const enableBreakingNewsTest        = EnableBreakingNewsTest;
    const enableNewSmartBanner        = EnableNewSmartBanner;
    const enableNewFooterUIWithCMPIntegration = EnableNewFooterUIWithCMPIntegration;
    const usPrivacyManagerId            = UsPrivacyManagerId;
    const sourcePointCMPAccountId       = SourcePointCMPAccountId;
    const sourcePointCMPPropertyId      = SourcePointCMPPropertyId;
    const sourcePointCMPPropertyHref    = SourcePointCMPPropertyHref;
    const sourcePointCMPBaseEndpoint    = SourcePointCMPBaseEndpoint;
    const sourcePointCMPCampaignEnv     = SourcePointCMPCampaignEnv;
    const pressLinkActive               = PressLinkActive;
    const airQualityAttributionUrl      = AirQualityAttributionUrl;
    const disclosureRequestEmail        = DisclosureRequestEmail;
    const deletionRequestEmail          = DeletionRequestEmail;
    const privacyRightsEmail             = PrivacyRightsEmail;
    const ccpaInfoEmail                 = CcpaInfoEmail;
    const privacyRightsLink             = PrivacyRightsLink;
    const privacyStatement              = PrivacyStatement;
    const myIdfaLink                    = MyIdfaLink;
    const authClientSettings            = AuthClientSettings;
    const isAuthV2Enabled               = IsAuthV2Enabled;
    const isAqiV2Enabled                = IsAqiV2Enabled;
    const isOutdoorSportsEnabled        = IsOutdoorSportsEnabled;
    const pageIdsToSupportLocationChanges = PageContentDoesNotRequireActiveLocationData;
    const trackerExclusionList          = TrackerExclusionList;
    const newPrivacyPolicyMockData      = NewPrivacyPolicyMockData;
    const persistent320x50AdForMobileWeb = Persistent320x50AdForMobileWeb;
    const isComplianceApiV3Enabled      = IsComplianceApiV3Enabled;
    const isHurricaneSectionEnabled     = IsHurricaneSectionEnabled;
    const enable10DayForecastPageOnWeb2   = Enable10DayForecastPageOnWeb2;
    const enableHourlyForecastPageOnWeb2   = EnableHourlyForecastPageOnWeb2;
    const enableTrafficCamerasPageOnWeb2   = EnableTrafficCamerasPageOnWeb2;
    const enableWeatherCamerasPageOnWeb2   = EnableWeatherCamerasPageOnWeb2;
    const enableMapsPageOnWeb2   = EnableMapsPageOnWeb2;
    const enableNewsLandingPageOnWeb2   = EnableNewsLandingPageOnWeb2;
    const enableLifePollenPageOnWeb2   = EnableLifePollenPageOnWeb2;
    const enableLifeColdFluPageOnWeb2   = EnableLifeColdFluPageOnWeb2;
    const enableVideoLandingPageOnWeb2   = EnableVideoLandingPageOnWeb2;
    const enableAlertsAQIPageOnWeb2   = EnableAlertsAQIPageOnWeb2;
    const enableAlertsPollenPageOnWeb2 = EnableAlertsPollenPageOnWeb2;
    const enableAlertsAllPageOnWeb2 = EnableAlertsAllPageOnWeb2;
    const enableAlertsSevereStormPageOnWeb2   = EnableAlertsSevereStormPageOnWeb2;
    const enableAlertsLightningPageOnWeb2   = EnableAlertsLightningPageOnWeb2;
    const enableFireAlertsPageOnWeb2   = EnableFireAlertsPageOnWeb2;
    const enableHurricaneAlertsPageOnWeb2   = EnableHurricaneAlertsPageOnWeb2;
    const enableAlertsWeatherPageOnWeb2   = EnableAlertsWeatherPageOnWeb2;
    const enableSourceParameterOnStoriesLinks     = EnableSourceParameterOnStoriesLinks;
    const enableEducationPageOnWeb2  = EnableEducationPageOnWeb2;
    const enableAirQualityLandingPageOnWeb2 = EnableAirQualityLandingPageOnWeb2;
    const enableFaqAndFaqGeneralPageOnWeb2 = EnableFaqAndFaqGeneralPageOnWeb2;
    const gTMediaCampaignIsEnabled      = GTMediaCampaign.IsEnabled;
    const firebaseConfig                = Firebase;
    const comscoreConfig                = Comscore;
    const boltiveConfig                 = Boltive;
    const gamTestAdConfig               = GamTestAd;
    const homePagePixelImgSourceUrl     = GTMediaCampaign.HomePagePixelImgSourceUrl;
    const appDownloadPagePixelImgSourceUrl  = GTMediaCampaign.AppDownloadPagePixelImgSourceUrl;
    const platform = Platform;
    const hurricaneOutlookDetailsImages = HurricaneOutlookDetailsImages;

    const enableAds                            = getAreAdsEnabled(isComplianceRequirementsDisableAd, areAdsDisable, appPlatform, appPlatformCode);
    const enableNotifications                  = getAreNotificationsEnabled(appPlatform, appPlatformCode);
    const enableUseOfCookiesNotification       = getAreUseOfCookiesNotificationEnabled(getAreNotificationsEnabled(appPlatform, appPlatformCode), appPlatform, appPlatformCode);
    const enableTermsAndConditionsNotification = getAreTermsAndConditionsNotificationEnabled(getAreNotificationsEnabled(appPlatform, appPlatformCode), appPlatform, appPlatformCode);
    const enableTaboola                        = isTaboolaAdEnabled(isComplianceRequirementsDisableAd, Taboola?.Disabled, Taboola?.LightHouseQueryParamDisableValue);
    const os                                   = getOs();
    const osVersion                            = getOsVersion();
    const userLocale                           = getUserLocale();
    const countryCode                          = getCountryCodeFromLocale(userLocale);
    const languageCode                         = getLanguageCodeFromLocale(userLocale);

    const appInstanceIdFromUser = getAppInstanceIdFromUserCookie(userStateCookieName, allowStorageOrCookieAccess);
    const appInstanceId         = appInstanceIdFromUser;
    const alertsPollerDelaySeconds      = PollingTimes?.Alerts;
    const hourlyPollerDelaySeconds      = PollingTimes?.HourlyForecast;
    const dailyForecastPollerDelaySeconds = PollingTimes?.DailyForecast;

    const stdParameters = {
        appId,
        application: appId,
        appInstanceId: appInstanceId,
        appVersion,
        cultureInfo: userLocale,
        formFactor: formFactor,
        os,
        osVersion,
        userAgent: nAgt
    };

    const appState = {
        adTarget,
        appId,
        appInstanceId,
        appVersion,
        authId,
        countryCode,
        enableAds,
        enableNotifications,
        enableTaboola,
        enableTermsAndConditionsNotification,
        enableUseOfCookiesNotification,
        fireBaseUrl,
        formFactor,
        adTargetingBaseUrl,
        hurricaneBaseUrl,
        hurricaneOutlookBaseUrl,
        alertsBaseUrl,
        outdoorSportsBaseUrl,
        notificationsBaseUrl,
        lightningBaseUrl,
        loggingBaseUrl,
        aqiBaseUrl,
        forecastBaseUrl,
        locationBaseUrl,
        obsBaseUrl,
        pollenBaseUrl,
        almanacBaseUrl,
        uvBaseUrl,
        contentMgmtBaseUrl,
        legalBaseUrl,
        storiesBaseUrl,
        snowSkiBaseUrl,
        videoBaseUrl,
        feedbackBaseUrl,
        sparkMapBaseUrl,
        forecastIconResourceList,
        forecastIconSize,
        forecastIconSet,
        languageCode,
        mapBaseUrl,
        mapboxAccessToken,
        os,
        osVersion,
        platformCode,
        secret,
        tileBaseUrl,
        userAgent: nAgt,
        userLocale,
        user,
        doNotTrack,
        defaultMapLayerId: DefaultMapLayerId || 'radar',
        defaultAdRefeshIntervalSeconds: DefaultAdRefeshIntervalSeconds || 300, //Default value is 300 seconds (5 minutes)
        leadingEdgeDebounceDelay: LeadingEdgeDebounceDelay || 3,
        mapsPageRefreshTimer: MapsPageRefreshTimer || 60,
        videoPreRollUrls: VideoPreRollUrls,
        videoPrerollAdClient: VideoPrerollAdClient,
        videoPrerollOffset: VideoPrerollOffset,
        videoPrerollSkipOffset: VideoPrerollSkipOffset,
        videoPrerollVPaidControls: VideoPrerollVPaidControls,
        videoPrerollAutoplayAdsMuted: VideoPrerollAutoplayAdsMuted,
        featuredVideoPrerollPlacementId: FeaturedVideoPrerollPlacementId,
        nationalOutlookVideoPrerollPlacementId: NationalOutlookVideoPrerollPlacementId,
        storiesVideoPrerollPlacementId: StoriesVideoPrerollPlacementId,
        botAppInstanceId: BotAppInstanceId,
        educationVideoPageURL,
        iosAppDownloadLink: IOSAppDownloadLink,
        macosAppDownloadLink: MacosAppDownloadLink,
        androidAppDownloadLink: AndroidAppDownloadLink,
        desktopAppDownloadLink: DesktopAppDownloadLink,
        enableDesktopAppDownloadLink: EnableDesktopAppDownloadLink,
        supportEmailAddress: SupportEmailAddress,
        taboolaInfo: TaboolaInfo,
        activeRoute,
        kochavaClickURL,
        iosSmartAppBannerURL,
        androidSmartAppBannerURL,
        sourceProviderId,
        webBackEndBaseURL,
        videoAdRules: VideoAdRules,
        emLoginUrl,
        appKey: WB_AppKey,
        alertsPollerDelaySeconds,
        hourlyPollerDelaySeconds,
        emailOptInDialogIsEnabled,
        emailOptInDialogScriptSrcUrl,
        allowStorageOrCookieAccess,
        breakingNewsTestCategory,
        enableBreakingNewsTest,
        enableNewSmartBanner,
        enableNewFooterUIWithCMPIntegration,
        usPrivacyManagerId,
        sourcePointCMPAccountId,
        sourcePointCMPPropertyId,
        sourcePointCMPPropertyHref,
        sourcePointCMPBaseEndpoint,
        sourcePointCMPCampaignEnv,
        pressLinkActive,
        dailyForecastPollerDelaySeconds,
        airQualityAttributionUrl,
        disclosureRequestEmail,
        deletionRequestEmail,
        privacyRightsEmail,
        ccpaInfoEmail,
        privacyRightsLink,
        privacyStatement,
        myIdfaLink,
        pageIdsToSupportLocationChanges,
        trackerExclusionList,
        authClientSettings,
        isAuthV2Enabled,
        isAqiV2Enabled,
        isOutdoorSportsEnabled,
        newPrivacyPolicyMockData,
        persistent320x50AdForMobileWeb,
        isComplianceApiV3Enabled,
        isHurricaneSectionEnabled,
        enable10DayForecastPageOnWeb2,
        enableHourlyForecastPageOnWeb2,
        enableTrafficCamerasPageOnWeb2,
        enableWeatherCamerasPageOnWeb2,
        enableMapsPageOnWeb2,
        enableNewsLandingPageOnWeb2,
        enableLifePollenPageOnWeb2,
        enableLifeColdFluPageOnWeb2,
        enableVideoLandingPageOnWeb2,
        enableAlertsAQIPageOnWeb2,
        enableAlertsPollenPageOnWeb2,
        enableAlertsAllPageOnWeb2,
        enableAlertsSevereStormPageOnWeb2,
        enableAlertsLightningPageOnWeb2,
        enableFireAlertsPageOnWeb2,
        enableHurricaneAlertsPageOnWeb2,
        enableAlertsWeatherPageOnWeb2,
        enableEducationPageOnWeb2,
        enableAirQualityLandingPageOnWeb2,
        enableFaqAndFaqGeneralPageOnWeb2,
        enableSourceParameterOnStoriesLinks,
        gTMediaCampaignIsEnabled,
        platform,
        homePagePixelImgSourceUrl,
        appDownloadPagePixelImgSourceUrl,
        hurricaneOutlookDetailsImages,
        stdParameters,
        comscoreConfig,
        firebaseConfig,
        boltiveConfig,
        gamTestAdConfig
    };

    return appState as unknown as IAppState;
};
